/**
 * Label Component
 *
 * A comprehensive, accessible form label component for the BiteWise application.
 * Supports various styles, required indicators, and follows WCAG 2.1 AA guidelines.
 * Integrates with the existing design system and theme.
 *
 * @module components/ui/Label
 */

import React, { forwardRef } from 'react';
import { cn } from '@/utils/cn';
import { useTheme } from '@/hooks/useTheme';

/**
 * Label component props interface
 */
export interface LabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  /** Label variant for different visual styles */
  variant?: 'default' | 'inline' | 'floating' | 'stacked';
  /** Label size */
  size?: 'sm' | 'md' | 'lg';
  /** Whether the associated field is required */
  required?: boolean;
  /** Whether the label should be visually hidden but accessible to screen readers */
  srOnly?: boolean;
  /** Custom required indicator */
  requiredIndicator?: React.ReactNode;
  /** Whether to show optional indicator */
  showOptional?: boolean;
  /** Custom optional indicator */
  optionalIndicator?: React.ReactNode;
  /** Icon to display with the label */
  icon?: React.ReactNode;
  /** Tooltip or help text */
  tooltip?: string;
  /** Whether the label is disabled */
  disabled?: boolean;
  /** Error state */
  error?: boolean;
  /** Success state */
  success?: boolean;
  /** Warning state */
  warning?: boolean;
}

/**
 * Accessible Label component with comprehensive styling and state support
 *
 * @example
 * ```tsx
 * <Label
 *   htmlFor="email"
 *   required
 *   size="md"
 *   tooltip="Enter your email address"
 * >
 *   Email Address
 * </Label>
 * ```
 */
export const Label = forwardRef<HTMLLabelElement, LabelProps>(
  ({
    className,
    variant = 'default',
    size = 'md',
    required = false,
    srOnly = false,
    requiredIndicator,
    showOptional = false,
    optionalIndicator,
    icon,
    tooltip,
    disabled = false,
    error = false,
    success = false,
    warning = false,
    children,
    ...props
  }, ref) => {
    const { isDark } = useTheme();
    // Base label classes
    const baseClasses = 'font-medium transition-colors duration-200';

    // Variant classes
    const variantClasses = {
      default: 'block',
      inline: 'inline-flex items-center',
      floating: 'absolute left-3 transition-all duration-200 pointer-events-none',
      stacked: 'block mb-1'
    };

    // Size classes
    const sizeClasses = {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-base'
    };

    // State-based color classes
    const getColorClasses = () => {
      if (disabled) {
        return isDark ? 'text-secondary-500' : 'text-secondary-400';
      }
      if (error) {
        return isDark ? 'text-red-400' : 'text-red-600';
      }
      if (warning) {
        return isDark ? 'text-yellow-400' : 'text-yellow-600';
      }
      if (success) {
        return isDark ? 'text-green-400' : 'text-green-600';
      }
      return isDark ? 'text-secondary-300' : 'text-secondary-700';
    };

    // Screen reader only classes
    const srOnlyClasses = 'sr-only';

    // Default required indicator
    const defaultRequiredIndicator = (
      <span className="text-red-500 ml-0.5" aria-label="required">
        *
      </span>
    );

    // Default optional indicator
    const defaultOptionalIndicator = (
      <span className={cn(
        "ml-1 font-normal",
        isDark ? "text-secondary-400" : "text-secondary-500"
      )}>
        (optional)
      </span>
    );

    // Tooltip component
    const TooltipIcon = () => (
      <span
        className={cn(
          "ml-1 inline-flex items-center justify-center w-4 h-4 text-xs cursor-help",
          isDark
            ? "text-secondary-400 hover:text-secondary-300"
            : "text-secondary-500 hover:text-secondary-700"
        )}
        title={tooltip}
        aria-label={`Help: ${tooltip}`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-4 h-4"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9.879 7.519c0-1.105.908-2.001 2.029-2.001s2.029.896 2.029 2.001-.908 2.001-2.029 2.001-2.029-.896-2.029-2.001zM12 15.75h.007v.008H12v-.008z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </span>
    );

    return (
      <label
        ref={ref}
        className={cn(
          baseClasses,
          variantClasses[variant],
          sizeClasses[size],
          getColorClasses(),
          srOnly && srOnlyClasses,
          disabled && 'cursor-not-allowed',
          !disabled && 'cursor-pointer',
          className
        )}
        {...props}
      >
        {/* Icon */}
        {icon && (
          <span className="mr-2 flex-shrink-0">
            {icon}
          </span>
        )}

        {/* Label Text */}
        <span className="flex items-center">
          {children}

          {/* Required Indicator */}
          {required && (
            requiredIndicator || defaultRequiredIndicator
          )}

          {/* Optional Indicator */}
          {!required && showOptional && (
            optionalIndicator || defaultOptionalIndicator
          )}

          {/* Tooltip */}
          {tooltip && <TooltipIcon />}
        </span>
      </label>
    );
  }
);

Label.displayName = 'Label';

/**
 * FormLabel component - a specialized label for form fields
 * Automatically handles required/optional states and accessibility
 */
export interface FormLabelProps extends Omit<LabelProps, 'variant'> {
  /** The form field this label is associated with */
  htmlFor: string;
  /** Whether to show the optional indicator when not required */
  showOptionalByDefault?: boolean;
}

export const FormLabel = forwardRef<HTMLLabelElement, FormLabelProps>(
  ({
    required = false,
    showOptional,
    showOptionalByDefault = false,
    ...props
  }, ref) => {
    return (
      <Label
        ref={ref}
        variant="stacked"
        required={required}
        showOptional={showOptional ?? (!required && showOptionalByDefault)}
        {...props}
      />
    );
  }
);

FormLabel.displayName = 'FormLabel';

/**
 * InlineLabel component - for inline form layouts
 */
export const InlineLabel = forwardRef<HTMLLabelElement, LabelProps>(
  (props, ref) => {
    return (
      <Label
        ref={ref}
        variant="inline"
        {...props}
      />
    );
  }
);

InlineLabel.displayName = 'InlineLabel';

/**
 * FloatingLabel component - for floating label inputs
 */
export interface FloatingLabelProps extends LabelProps {
  /** Whether the label should float (when input has value or is focused) */
  floating?: boolean;
}

export const FloatingLabel = forwardRef<HTMLLabelElement, FloatingLabelProps>(
  ({
    floating = false,
    size = 'md',
    className,
    ...props
  }, ref) => {
    const { isDark } = useTheme();

    // Floating label positioning classes
    const floatingClasses = floating
      ? cn(
          'top-0 left-3 -translate-y-1/2 px-1',
          isDark ? 'bg-secondary-800' : 'bg-white',
          size === 'sm' && 'text-xs',
          size === 'md' && 'text-xs',
          size === 'lg' && 'text-sm'
        )
      : cn(
          'top-1/2 left-3 -translate-y-1/2',
          size === 'sm' && 'text-sm',
          size === 'md' && 'text-base',
          size === 'lg' && 'text-lg'
        );

    return (
      <Label
        ref={ref}
        variant="floating"
        size={size}
        className={cn(floatingClasses, className)}
        {...props}
      />
    );
  }
);

FloatingLabel.displayName = 'FloatingLabel';

export default Label;